import type { LeadActivityKind } from './leadActivity';
import type { Membership } from './types';

/**
 * Which campaign a hand-logged email belonged to.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Measured 28 August 2026 against the live database: TEN deals, and not one
 * traceable to a campaign. Every send that preceded them was logged by hand,
 * and hand-logged emails could not carry a list or a copy set — neither
 * `log_lead_activity` nor the table's own `lead_activity_mirror_shape`
 * constraint allowed it. So "which campaign produced this deal" was
 * structurally unanswerable for exactly the sends that produce deals.
 *
 * Migration 20261217000100 opened the path. This module is the small amount of
 * judgement in front of it, kept out of the component so it can be tested
 * without rendering anything.
 *
 * ── "Log it, then ask" ─────────────────────────────────────────────────────
 *
 * Kyu's call, over putting two dropdowns on a one-click button. The send
 * records instantly, exactly as before; the campaign is an optional second
 * click on the row just written. The fast path stays fast, and the prompt only
 * appears when there is genuinely something to pick.
 */

/** A campaign a particular lead could honestly claim to be part of. */
export interface AttributionChoice {
  listId: string;
  /** The copy set on that membership, if it has one. Used as the default. */
  copySetId: string | null;
}

/**
 * The lists this lead is actually ON.
 *
 * The database refuses a list the lead is not a member of, so offering one
 * would be offering a refusal. Deriving the options from the same fact the
 * rule is written against is what keeps the picker and the database agreeing.
 *
 * Deduplicated by list: a lead with two memberships on one list — which the
 * table permits — must not produce the same campaign twice in a dropdown. The
 * first membership wins, because there is nothing to choose between them and a
 * stable answer beats an arbitrary one.
 */
export function choicesForLead(
  memberships: readonly Membership[],
  leadId: string,
): AttributionChoice[] {
  const seen = new Set<string>();
  const out: AttributionChoice[] = [];
  for (const membership of memberships) {
    if (membership.lead_id !== leadId || seen.has(membership.list_id)) {
      continue;
    }
    seen.add(membership.list_id);
    out.push({ listId: membership.list_id, copySetId: membership.copy_set_id });
  }
  return out;
}

/**
 * Kinds whose campaign is a meaningful thing to record.
 *
 * A NOTE is not a send and has no campaign — offering the picker after one
 * would be asking a question with no true answer. A REPLY does: the copy it
 * answered is exactly what makes "which copy gets replies" answerable, and it
 * is the same question one level along.
 */
const ATTRIBUTABLE: readonly LeadActivityKind[] = [
  'email_sent',
  'call',
  'text',
  'linkedin',
  'meeting',
  'reply_received',
];

export function isAttributableKind(kind: LeadActivityKind): boolean {
  return ATTRIBUTABLE.includes(kind);
}

/**
 * Whether to put the question in front of somebody at all.
 *
 * Two conditions, and both matter. A lead on no list has nothing to pick, so
 * the prompt would be a dead control. A note has no campaign, so the prompt
 * would be a question with no true answer. A prompt that appears when it
 * cannot be answered is how people learn to dismiss prompts without reading
 * them — which would cost us the ones that do matter.
 */
export function shouldOfferAttribution(
  kind: LeadActivityKind,
  choices: readonly AttributionChoice[],
): boolean {
  return isAttributableKind(kind) && choices.length > 0;
}

/**
 * What the picker starts on.
 *
 * A lead on exactly ONE list gets it preselected — there is only one true
 * answer and making somebody choose it is ceremony. A lead on several starts
 * BLANK, because picking the first would be a guess the reader could easily
 * mistake for a fact, and the whole point of this is to stop guessing about
 * attribution.
 */
export function defaultChoice(choices: readonly AttributionChoice[]): AttributionChoice | null {
  return choices.length === 1 ? (choices[0] ?? null) : null;
}

/** The copy set to offer alongside a chosen campaign, or none. */
export function copySetForList(
  choices: readonly AttributionChoice[],
  listId: string,
): string | null {
  return choices.find((choice) => choice.listId === listId)?.copySetId ?? null;
}
